import { belegtext, t } from './sprache'
import type { Belegmarke, Vorgangsmarke, Zeilenherkunft } from './typen'

/**
 * Wie ein Wertenachweis auf seine Herkunft zeigt (2.14 Abschnitt 6.5a und
 * 6.5b, R433, R824).
 *
 * **Der Verweis steht im Namen.** Bis zum 2026-09-12 stand in dieser Spalte
 * für jede Zeile derselbe Satz „zum Beleg" — eine Spalte, in der zwanzigmal
 * dasselbe steht, trägt nichts bei und kostet trotzdem Platz. Jetzt trägt sie,
 * was die Herkunft **unterscheidet**: ihren Namen, wenn sie einen hat, sonst
 * ihre Nummer.
 *
 * **Auch eine gerechnete Zahl hat einen Namen** — seit dem 2026-09-27. Wo
 * keine Lieferung dahintersteht, steht dort die Rechenstufe:
 * *Kapitalkonsolidierung*, *Firmenwert fortschreiben*. Der Satz „dort gibt es
 * nichts Unterscheidendes zu zeigen" war der Irrtum, mit dem das
 * Platzhalterwort fünfzehn Tage überlebt hat.
 *
 * **Und eine Zeile kann mehrere haben.** Wer die Bewegungsart abwählt, bekommt
 * Zugang und Abschreibung in einer Zeile; sie kommen dann aus zwei Quellen.
 * Bis zum 2026-09-27 nannte die Zeile in diesem Fall *keine* — richtig daran
 * war, daß ein Verweis auf eine von zweien eine Herkunft behauptet, die nicht
 * stimmt; falsch war die Folgerung. Sie nennt jetzt **alle**.
 *
 * **Die beiden Nachweise teilen sich diese Stelle**, und das ist der Grund,
 * warum sie hier steht und nicht in einem der beiden Bildschirme: Bis zum
 * 2026-09-13 hatte nur der Bilanznachweis sie, und der Nachweis der
 * Kapitalflussrechnung zeigte weiter zwanzigmal „zum Beleg". Dieselbe Spalte
 * stellt in beiden dieselbe Frage; eine Regel, die an zwei Stellen steht, ist
 * zwei Regeln.
 */

/** Ein Eintrag der Belegspalte — eine Herkunft, fertig zum Anzeigen. */
export type Belegverweis = {
  /** Eindeutig innerhalb der Zeile. */
  schluessel: string
  /** Was in der Zelle steht: Belegname, Belegnummer oder Name der Rechenstufe. */
  aufschrift: string
  /**
   * Was beim Zeigen erscheint — die Belegnummer, die der Name verdrängt hat,
   * und der Anteil.
   *
   * `undefined`, wo es nichts nachzureichen gibt.
   */
  titel: string | undefined
  /**
   * Was Tastatur und Vorlesehilfe bekommen: Aufschrift **und** das, was sonst
   * nur im Tooltip stünde.
   *
   * **Die Auflage zur Tooltip-Entscheidung vom 2026-09-27** (2.14 Abschnitt
   * 6.5b): Ein Tooltip ist bei Tastatur und Touch nicht erreichbar. Nummer und
   * Anteil hängen deshalb zusätzlich an der Bedienhilfe-Beschriftung — der
   * Tooltip bleibt die bequeme Abkürzung für die Maus, nicht die einzige
   * Quelle.
   */
  beschriftung: string
  /** Wohin der Sprung führt — Lauf, Vorgang, Beleg. */
  lauf: number | null
  vorgangId: number | null
  belegId: number | null
}

/**
 * Zwei Herkunftslisten werden eine (R824).
 *
 * **Gleiche Quellen werden addiert, nicht aneinandergereiht.** Zwölf Perioden
 * aus derselben Lieferung sind eine Herkunft mit einem Anteil — zwölfmal
 * denselben Namen untereinander zu schreiben, wäre genau die Spalte, die
 * R433 abgeschafft hat.
 *
 * **Was sich zu null aufhebt, fällt weg.** Eine Quelle, die nichts beiträgt,
 * ist keine Herkunft dieser Zahl, und ein Verweis auf sie führte ins Leere.
 *
 * **Sie steht hier und nicht im Bildschirm**, weil sie die Kehrseite dessen
 * ist, was der Rechenkern beim Verdichten tut: Dieselbe Regel an zwei Stellen
 * ist zwei Regeln, und bis zum 2026-09-27 war die zweite falsch — dort blieb
 * die Herkunft der *ersten* Zeile stehen.
 */
export function herkuenfteZusammen(
  eine: readonly Zeilenherkunft[] | undefined,
  andere: readonly Zeilenherkunft[] | undefined,
): Zeilenherkunft[] {
  const nachQuelle = new Map<string, Zeilenherkunft>()

  for (const h of [...(eine ?? []), ...(andere ?? [])]) {
    const schluessel = `${h.belegId ?? ''}\u001f${h.vorgangId ?? ''}`
    const vorher = nachQuelle.get(schluessel)
    nachQuelle.set(
      schluessel,
      vorher === undefined ? h : { ...vorher, anteil: vorher.anteil + h.anteil },
    )
  }

  return [...nachQuelle.values()].filter((h) => h.anteil !== 0)
}

/** Die Marken eines Nachweises, greifbar nach ihrer Kennung. */
export function belegmarken(belege: readonly Belegmarke[] | undefined): Map<number, Belegmarke> {
  return new Map((belege ?? []).map((b) => [b.id, b]))
}

/** Die Rechenstufen eines Nachweises, greifbar nach ihrem Code. */
export function vorgangsmarken(
  vorgaenge: readonly Vorgangsmarke[] | undefined,
): Map<string, Vorgangsmarke> {
  return new Map((vorgaenge ?? []).map((v) => [v.code, v]))
}

/**
 * Die Einträge der Belegspalte für eine Zeile — einer je Herkunft (R824).
 *
 * @param anteiltext Wie ein Anteil geschrieben wird. Er steht nur dann dabei,
 *   wenn die Zeile **mehrere** Herkünfte hat: Bei einer einzigen ist der
 *   Anteil der Betrag der Zeile, und der steht in der Spalte daneben.
 */
export function belegverweise(
  marken: Map<number, Belegmarke>,
  stufen: Map<string, Vorgangsmarke>,
  sprache: string,
  herkuenfte: readonly Zeilenherkunft[] | undefined | null,
  anteiltext: (anteil: number) => string,
): Belegverweis[] {
  const alle = herkuenfte ?? []
  const mehrere = alle.length > 1

  return alle.map((h, i) => {
    const marke = h.belegId === null ? undefined : marken.get(h.belegId)
    // **Der gespeicherte Name wird mit Namen gelesen, nicht umgeschrieben**
    // (7.2 Abschnitt 20, R838): „Saldenliste EH-000005-57 202612" ist die
    // Nachweisspur des Imports (R388) und bleibt so im Beleg; die Zelle
    // zeigt „Saldenliste Holding AG Dezember 2026" — mit Namen und, seit dem
    // 2026-09-30, mit Monat und Jahr (7.2 Abschnitt 21, R839). Hier, weil
    // beide Nachweise ihre Belegspalte aus dieser Stelle beziehen.
    const roh = marke?.name?.trim()
    const name = roh ? belegtext(roh) : undefined
    const stufe = h.vorgangscode === null ? undefined : stufen.get(h.vorgangscode)

    // Der Name der Lieferung, sonst ihre Nummer, sonst der Name der
    // Rechenstufe. Das Platzhalterwort steht nur noch da, wo weder das eine
    // noch das andere geliefert wurde — ein Fall, den es nicht geben sollte.
    const aufschrift =
      name || marke?.nummer || stufe?.namen[sprache] || stufe?.namen.de || t('nachweis.zumbeleg')

    // **Der Name ersetzt die Nummer nicht, er tritt vor sie** (R433): Wer
    // einen Beleg sucht, sucht ihn über die Nummer.
    const nummer = marke !== undefined && name ? `${marke.nummer} — ${name}` : undefined
    const anteil = mehrere ? t('nachweis.anteil', { betrag: anteiltext(h.anteil) }) : undefined
    const nachreichen = [nummer, anteil].filter((x) => x !== undefined)

    return {
      schluessel: `${h.belegId ?? 'v'}-${h.vorgangId ?? ''}-${i}`,
      aufschrift,
      titel: nachreichen.length > 0 ? nachreichen.join(' · ') : undefined,
      beschriftung: [aufschrift, ...nachreichen].join(' · '),
      lauf: h.laufId,
      vorgangId: h.vorgangId,
      belegId: h.belegId,
    }
  })
}
